import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLandingConfigStatic, defaultLandingConfig, defaultSections } from '../hooks/useLandingConfig';
import { benefitsService } from '../services/benefits';
import { storesService } from '../services/stores';
import { API_BASE_URL } from '../config';

import BrandScroller from '../components/ui/BrandScroller';
import { FlipCard } from '../components/ui/FlipCard';
import { VondiLogo } from '../components/ui/VondiLogo';
import { VD, GRAD, SOMBRA, RADIO, textoDegradado } from '../lib/brand';
import MuestrarioPrecios from '../components/MuestrarioPrecios';
import CalculadoraAhorro from '../components/CalculadoraAhorro';

/* ─── CSS injected once ──────────────────────────────────── */
/* Nota: los keyframes de la marquesina ya no viven aquí; los inyecta el
   componente compartido BrandScroller (antes había una copia local). */
const CSS = `
@keyframes mp-brand-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
@keyframes mp-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes mp-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
.mp-no-scrollbar::-webkit-scrollbar { display: none; }
.mp-no-scrollbar { scrollbar-width: none; }
`;

/*
  Antes aquí vivían `ACCENT = '#FF5252'` y un degradado a 135° que no existen en
  el manual: eran del sistema anterior. El acento es el naranja de marca y el
  degradado es el vivo, a 100°, como toda la app (BRAND.md §1).
*/
const ACCENT          = VD.naranja;
const ACCENT_GRADIENT = GRAD.vivo;

/* ─── Palette fallback for stores without color ─────────── */
const PALETTE = ['#1A1A1A','#2563EB','#16A34A','#EA580C','#7C3AED','#0891B2','#D946EF','#CA8A04'];

/* ─── Carousel constants ─────────────────────────────────── */
const CARD_W = 200;
const GAP    = 14;
const PEEK   = 56;
const STEP   = CARD_W + GAP;

/* ─── Discount Card ───────────────────────────────────────────────────────
   La tarjeta de descuento del carrusel público es la misma flip card líquida
   que ve el miembro: mismo componente, mismo formato sagrado. Antes era una
   copia con su propia deriva de estilos (200×340, radio 24, texto a la
   izquierda), que es justo lo que el manual prohíbe.

   No voltea en el landing: aquí es escaparate, y el detalle vive detrás del
   registro.

   Recibe un beneficio ya aplanado, no una tienda: desde el 30-ago-2026 el
   carrusel pinta una tarjeta por beneficio y no una por tienda. */
function DiscountCard({ card }) {
  return (
    <div style={{ width: 200, flexShrink: 0 }}>
      <FlipCard flippable={false} benefit={card} />
    </div>
  );
}

/* Nota: la sección "Destacados" (promo_carousel) se retiró el 11-ago-2026.
   Estaba apagada en producción (visible: false) desde antes, así que su editor
   en el admin solo ocupaba interfaz que nadie usaba. Los datos que quedaran
   guardados en landing_config siguen ahí, inertes: si algún día hiciera falta,
   se recupera del historial de git. */


/* ─── Carrusel infinito genérico ─────────────────────────── */
function InfiniteCarousel({ items, renderItem, label, mostrarContador = true }) {
  const N        = items.length;
  const triple   = [...items, ...items, ...items];
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const adjusting           = useRef(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || N === 0) return;
    el.scrollLeft = N * STEP;
  }, [N]);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || adjusting.current || N === 0) return;
    const idx = Math.round(el.scrollLeft / STEP);
    setActive(((idx % N) + N) % N);
    if (idx <= N - 1) {
      adjusting.current = true;
      el.scrollLeft += N * STEP;
      requestAnimationFrame(() => { adjusting.current = false; });
    } else if (idx >= 2 * N) {
      adjusting.current = true;
      el.scrollLeft -= N * STEP;
      requestAnimationFrame(() => { adjusting.current = false; });
    }
  }, [N]);

  const goTo = useCallback((i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: (N + i) * STEP, behavior: 'smooth' });
  }, [N]);

  /* Auto-avance del carrusel (se pausa al interactuar) */
  useEffect(() => {
    const el = trackRef.current;
    if (!el || N <= 1) return;
    let paused = false;
    let resumeTimer;
    const pause = () => { paused = true; clearTimeout(resumeTimer); };
    const resume = () => { clearTimeout(resumeTimer); resumeTimer = setTimeout(() => { paused = false; }, 4000); };
    el.addEventListener('pointerdown', pause);
    el.addEventListener('pointerup', resume);
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchend', resume, { passive: true });
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    const id = setInterval(() => {
      if (!paused) el.scrollBy({ left: STEP, behavior: 'smooth' });
    }, 3000);
    return () => {
      clearInterval(id);
      clearTimeout(resumeTimer);
      el.removeEventListener('pointerdown', pause);
      el.removeEventListener('pointerup', resume);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
    };
  }, [N]);

  if (N === 0) return null;

  return (
    <section style={{ paddingTop: 6, paddingBottom: 24, background: '#fff' }}>
      <div style={{ padding: '0 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        {/* Etiqueta del manual: Inter 700, mayúsculas, tracking amplio (§2). */}
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: VD.tinta }}>
          {label}
        </span>
        {/* El «13 / 22» se oculta en el carrusel de descuentos (Iván,
            30-ago-2026). Con una tarjeta por beneficio el total es alto y la
            cuenta leía como inventario, no como promesa. */}
        {mostrarContador && <span style={{ fontSize: 12, color: VD.pizarra }}>{active + 1} / {N}</span>}
      </div>
      <div ref={trackRef} onScroll={onScroll} className="mp-no-scrollbar" style={{
        display: 'flex', gap: GAP, overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        paddingLeft: PEEK, paddingRight: PEEK, paddingBottom: 14,
      }}>
        {triple.map((item, i) => {
          const logical  = i % N;
          const isActive = logical === active;
          return (
            <div key={i} style={{
              flex: `0 0 ${CARD_W}px`, scrollSnapAlign: 'center',
              transition: 'transform .25s ease, opacity .25s ease',
              transform: isActive ? 'scale(1)' : 'scale(0.93)',
              opacity:   isActive ? 1 : 0.7,
            }}>
              {renderItem(item, logical)}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 6 }}>
        {items.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{
            width: i === active ? 22 : 6, height: 6, borderRadius: RADIO.pill,
            background: i === active ? GRAD.vivo : VD.linea,
            transition: 'all .25s ease', border: 'none', padding: 0, cursor: 'pointer',
          }} />
        ))}
      </div>
    </section>
  );
}

/* ─── Section components ─────────────────────────────────── */

function HeroSection({ data, navigate }) {
  const heroTitle = (data?.heroTitle || 'PRECIOS OCULTOS!').replace(/\n/g, ' ');
  const heroCTA   = data?.heroCTA   || '¡ PAGA MENOS !';
  const footerCTA = data?.footerCTA || '¿Ya eres Miembro? Acceder';

  return (
    <>
      <div style={{ paddingTop: 96, paddingBottom: 20, background: '#fff' }}>
        <section style={{ padding: '44px 28px 8px', textAlign: 'center' }}>
          {/* El titular venía en Arial, literalmente: la fuente del sistema, no
              la de la marca. Poppins Bold con el tracking del manual (§2). */}
          <h1 className="font-display" style={{
            fontWeight: 700, fontSize: 'clamp(19px, 5.6vw, 27px)',
            lineHeight: 1.1, color: VD.tinta, margin: 0, letterSpacing: '-0.02em',
            whiteSpace: 'nowrap',
          }}>
            {heroTitle}
          </h1>
          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}>
            {/* Botón primario del manual (§6): degradado vivo, blanco, pill y
                sombra cálida difusa. Era negro sólido. */}
            <button onClick={() => navigate('/beneficios')} style={{
              height: 52, padding: '0 34px', borderRadius: RADIO.pill,
              background: GRAD.vivo, color: VD.blanco,
              fontSize: 15, fontWeight: 600, letterSpacing: '0.01em',
              border: 'none', cursor: 'pointer', boxShadow: SOMBRA.md,
            }}>
              {heroCTA}
            </button>
          </div>
        </section>
      </div>
      <div style={{ textAlign: 'center', padding: '0 24px 12px' }}>
        <button onClick={() => navigate('/login')} style={{
          fontSize: 13.5, color: VD.pizarra, background: 'none', border: 'none',
          cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
        }}>
          {footerCTA}
        </button>
      </div>
    </>
  );
}

function PayLessIntroSection({ data, navigate }) {
  const title           = data?.title           || 'Hoy puedes pagar menos.';
  const subtitle        = data?.subtitle        || 'Cada una de tus marcas favoritas tiene su propio beneficio exclusivo para ti';
  const quoteText       = data?.quoteText       || 'El precio esta especialmente diseñado, para que con tu primera compra te ahorres mas de lo que te cuesta el acceso.';
  const quoteButtonText = data?.quoteButtonText || 'ACCEDER';

  return (
    <section style={{ padding: '12px 0 8px', background: '#fff' }}>
      <div style={{ padding: '0 24px' }}>
        {/* H1 del manual: Poppins Bold. Estaba en 900, que el manual no usa. */}
        <h2 className="font-display" style={{ fontWeight: 700, fontSize: 'clamp(32px, 9vw, 42px)', letterSpacing: '-0.03em', lineHeight: 1.04, color: VD.tinta, margin: '0 0 12px' }}>
          {title}
        </h2>
        <p style={{ fontSize: 15, color: VD.pizarra, lineHeight: 1.55, margin: '0 0 14px', maxWidth: 320 }}>
          {subtitle}
        </p>
        <p style={{ fontSize: 15, color: VD.grafito, lineHeight: 1.65, margin: '0 0 28px', maxWidth: 340 }}>
          Desde{' '}
          <span style={{ color: ACCENT, fontWeight: 600 }}>Regalos</span>
          {' '}y{' '}
          <span style={{ color: ACCENT, fontWeight: 600 }}>Promociones exclusivas</span>
          {' '}en tus compras... hasta{' '}
          <span style={{ color: ACCENT, fontWeight: 600 }}>meses sin interés</span>
          {' '}y{' '}
          <span style={{ color: ACCENT, fontWeight: 600 }}>descuentos adicionales</span>
        </p>

        {/* Quote card. Era un degradado gris con sombra negra; ahora niebla con
            sombra cálida, que es la regla del manual (§4). */}
        <div style={{
          background: `linear-gradient(160deg, ${VD.niebla} 0%, ${VD.blanco} 100%)`,
          borderRadius: RADIO.card, padding: '26px 20px 24px',
          margin: '0 18px 0', border: `1px solid ${VD.linea}`, boxShadow: SOMBRA.md,
        }}>
          <p className="font-display" style={{
            fontWeight: 600, fontSize: 18, lineHeight: 1.5,
            color: VD.tinta, margin: '0 0 22px', letterSpacing: '-0.01em',
          }}>
            {quoteText}
          </p>
          {/* Negro, no degradado: Iván lo prefiere así en esta parte del
              landing, igual que el cierre de la sección. El degradado se
              reserva para el CTA principal de la barra y el de precio. */}
          <button onClick={() => navigate('/beneficios')} style={{
            height: 44, padding: '0 26px', borderRadius: RADIO.pill,
            background: VD.tinta, color: VD.blanco,
            fontSize: 14, fontWeight: 600, letterSpacing: '0.02em',
            border: 'none', cursor: 'pointer', boxShadow: SOMBRA.sm,
          }}>
            {quoteButtonText}
          </button>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ data }) {
  /* La descripción de qué es vondi la edita el admin (Iván, 30-ago-2026). El
     valor de abajo es solo lo que se ve mientras nadie la haya tocado. */
  const descripcion = data?.descripcion ||
    'Es un acceso el cual va a hacer que te ahorres mas dinero de lo que ya ibas a pagar, al acceder, tendrás acceso a vondi donde se te generará una tarjeta digital personal única (QR) con la cual la tienda podrá hacerte válido el beneficio exclusivo. Hoy en día vondi opera en Outlet Puebla. A partir del momento en donde pagas tu acceso tienes 1 mes de acceso, 7 días antes de la renovación te llegará un correo para que puedas cancelar o continuar.';

  const steps = data?.steps || [
    { n: 1, t: 'Accede y elige tu plan', d: 'Anual $650 o mensual $277. Todos los beneficios incluidos. Cancelas cuando quieras.' },
    { n: 2, t: 'Genera tu QR personal',    d: 'Tarjeta digital única con tiempo de expiración. Solo tú la puedes generar.' },
    { n: 3, t: 'La tienda valida y listo', d: 'El staff escanea, elige el beneficio y se canjea. Sin trámites adicionales.' },
  ];

  const [comoVisible, setComoVisible] = useState(false);
  const comoRef = useRef(null);

  useEffect(() => {
    const el = comoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setComoVisible(true); },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section style={{ padding: '12px 0 36px', background: '#fff' }}>
      <div style={{ padding: '0 24px' }}>
        {/* ¿Cómo Funciona? — grande con animación fade-up */}
        <h3
          ref={comoRef}
          className="font-display"
          style={{
            fontWeight: 700, fontSize: 'clamp(34px, 10vw, 46px)',
            letterSpacing: '-0.03em', color: VD.tinta, margin: '0 0 20px',
            opacity: comoVisible ? 1 : 0,
            transform: comoVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.65s ease, transform 0.65s ease',
          }}
        >
          ¿Cómo Funciona?
        </h3>

        {/* Una sola tarjeta, ahora en niebla con línea suave */}
        <div style={{
          background: `linear-gradient(160deg, ${VD.niebla} 0%, ${VD.blanco} 100%)`,
          borderRadius: RADIO.card, padding: '24px 20px 28px', marginBottom: 36,
          border: `1px solid ${VD.linea}`, boxShadow: SOMBRA.sm,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: VD.pizarra, marginBottom: 20, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Así de fácil...
          </div>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 14, alignItems: 'flex-start',
              marginBottom: i < steps.length - 1 ? 18 : 0,
              paddingBottom: i < steps.length - 1 ? 18 : 0,
              borderBottom: i < steps.length - 1 ? `1px solid ${VD.linea}` : 'none',
            }}>
              {/* Negros, como el resto de esta sección. */}
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: VD.tinta, color: VD.blanco,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 600, fontSize: 13, flexShrink: 0,
              }}>
                {s.n}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15.5, color: VD.tinta, lineHeight: 1.25, marginBottom: 4 }}>{s.t}</div>
                <div style={{ fontSize: 13.5, color: VD.pizarra, lineHeight: 1.55 }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>

        {/*
          El bloque de la marca. Decía «VONDI» escrito a mano, en mayúsculas y
          con la tipografía del cuerpo — justo lo que el manual §5 prohíbe: el
          wordmark va en minúsculas, en Poppins Bold y con la «o» como anillo.
          Ahora es el componente, que hace cumplir esas reglas por construcción.
        */}
        <div style={{ margin: '0 0 14px' }}>
          <VondiLogo variant="gradiente" size={28} />
        </div>
        <p style={{ fontSize: 14.5, color: VD.pizarra, lineHeight: 1.8, margin: 0 }}>
          {descripcion}
        </p>
      </div>
    </section>
  );
}

function SavingsCalculatorSection({ data }) {
  /*
    Los textos los edita el admin (Iván, 30-ago-2026: «texto editable», para no
    tener que entrar a sesión cada vez que quiera moverle). Lo de abajo es solo
    lo que se ve mientras nadie los haya tocado.
  */
  const titulo = data?.titulo ?? 'Calcula si te conviene comprar vondi';
  const subtitulo = data?.subtitulo
    ?? 'Suma las tiendas donde vas a comprar y compáralo con lo que cuesta el acceso.';
  const seccionRef = useRef(null);

  /* La pestaña de ahorro, cuando todavía está apagada, manda aquí con
     ?calculadora=1. Sin este desplazamiento el usuario caía arriba del landing
     y tenía que buscar la calculadora a mano. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!new URLSearchParams(window.location.search).has('calculadora')) return;
    const t = setTimeout(() => {
      seccionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section ref={seccionRef} style={{ padding: '36px 24px 44px', background: VD.niebla }}>
      <h3 className="font-display" style={{ fontWeight: 700, fontSize: 'clamp(26px, 7vw, 34px)', letterSpacing: '-0.03em', color: VD.tinta, margin: '0 0 6px' }}>
        {titulo}
      </h3>
      {subtitulo ? (
        <p style={{ fontSize: 14, color: VD.pizarra, margin: '0 0 28px', lineHeight: 1.5 }}>
          {subtitulo}
        </p>
      ) : <div style={{ height: 22 }} />}

      {/*
        El cálculo es el componente compartido, no una copia local como antes.
        Suma varias tiendas y compara el ahorro contra el precio del acceso, que
        es exactamente la pregunta del titular: si te conviene comprarlo.
      */}
      <CalculadoraAhorro compararConAcceso />

      {/*
        El muestrario va justo debajo de la calculadora (Iván, 13-ago-2026).

        El orden cuenta algo: la calculadora es una promesa en abstracto —«te
        ahorrarías $400»— y esto es la prueba con una prenda real y su precio.
        Primero el cálculo, luego el ejemplo que lo respalda.
      */}
      <div style={{ margin: '32px -24px 0' }}>
        <MuestrarioPrecios
          enLanding
          titulo={data?.muestrarioTitulo ?? 'Ejemplo de productos y precios reales'}
          subtitulo={data?.muestrarioSubtitulo ?? ''}
        />
      </div>
    </section>
  );
}

/*
  Aquí vivía `PricingSection`, el bloque «Solo con $277 desbloquea TODOS los
  beneficios». Se retiró completo, no solo se ocultó: dejarlo muerto invita a
  que alguien lo reviva sin saber por qué se quitó.

  El enlace a términos que llevaba dentro no se pierde: sigue en el pie legal
  de la página.
*/

function CTAReadySection({ data, navigate }) {
  const title      = data?.title      || '¿Estás listo?';
  const buttonText = data?.buttonText || 'Acceder';

  return (
    <section style={{ padding: '52px 28px 56px', background: '#fff', textAlign: 'center' }}>
      {/* El cierre del landing: el titular va en el degradado de marca, que es
          el recurso del manual para el texto que carga la promesa. */}
      <h2 className="font-display" style={{
        fontWeight: 700, fontSize: 'clamp(38px, 11vw, 52px)',
        letterSpacing: '-0.03em', lineHeight: 1.0,
        margin: '0 0 32px', ...textoDegradado(GRAD.marca),
      }}>
        {title}
      </h2>
      <button onClick={() => navigate('/beneficios')} style={{
        width: '100%', maxWidth: 320, height: 62, borderRadius: RADIO.pill,
        background: GRAD.vivo, color: VD.blanco,
        fontSize: 18, fontWeight: 600, border: 'none', cursor: 'pointer',
        boxShadow: SOMBRA.xl,
      }}>
        {buttonText}
      </button>
    </section>
  );
}

function InfoBlockSection({ data }) {
  if (!data?.title && !data?.body) return null;
  return (
    <section style={{ padding: '16px 22px 0', background: '#fff' }}>
      <div style={{ background: VD.niebla, borderRadius: RADIO.card, padding: '20px 18px', border: `1px solid ${VD.linea}` }}>
        {data.title && (
          <div className="font-display" style={{ fontWeight: 600, fontSize: 17, color: VD.tinta, letterSpacing: '-0.02em', marginBottom: 8 }}>
            {data.title}
          </div>
        )}
        {data.body && (
          <div style={{ fontSize: 13.5, color: VD.pizarra, lineHeight: 1.55 }}>{data.body}</div>
        )}
        {data.imageUrl && (
          <img src={data.imageUrl} alt={data.title || ''} style={{ marginTop: 12, width: '100%', borderRadius: RADIO.input, objectFit: 'cover', maxHeight: 200 }} />
        )}
      </div>
    </section>
  );
}

/* ─── Landing Page ──────────────────────────────────────── */
const LandingPage = () => {
  const navigate       = useNavigate();
  const [config, setConfig]           = useState(getLandingConfigStatic);
  const [storeCards, setStoreCards]   = useState([]);
  const [benefitCards, setBenefitCards] = useState([]);
  const [brandNames, setBrandNames]   = useState([]);
  const [carouselReady, setCarouselReady] = useState(false);
  const [scrolled, setScrolled]       = useState(false);
  const [brandIdx, setBrandIdx]       = useState(0);

  /* Sections: ordered, visible, and merged with any new default types */
  const sections = useMemo(() => {
    const loaded = config.sections ?? defaultSections;
    const loadedTypes = new Set(loaded.map(s => s.type));
    const injected = defaultSections.filter(s => !loadedTypes.has(s.type));
    return [...loaded, ...injected]
      .sort((a, b) => a.order - b.order)
      .filter(s => s.visible !== false);
  }, [config.sections]);

  /* El `?utm_source=` de los QR de canal ya no se rastrea aquí: lo recoge
     `useCapturaEnlace` en cualquier ruta, junto con los enlaces de closer. */

  /* Fetch landing config from server */
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/landing-config`)
      .then(r => r.json())
      .then(data => {
        if (data?.success && data?.data) {
          const d = data.data;
          setConfig(prev => ({
            ...prev,
            logos:      d.logos      ?? prev.logos,
            cards:      d.cards      ?? prev.cards,
            texts:      d.texts      ?? prev.texts,
            textBlocks: d.textBlocks ?? [],
            sections:   d.sections   ?? defaultSections,
          }));
        }
      })
      .catch(() => {});
  }, []);

  /* Fetch benefits + stores for discount carousel */
  useEffect(() => {
    Promise.all([
      benefitsService.getAll().catch(() => ({ data: [] })),
      storesService.getAll().catch(() => []),
    ]).then(([benefitsRes, storesRes]) => {
      const benefits = (benefitsRes?.data ?? []).filter(b => b.isActive !== false);
      const stores   = Array.isArray(storesRes) ? storesRes : (storesRes?.data ?? []);

      const colorMap = {};
      const textColorMap = {};
      const catMap   = {};
      const placeMap = {};
      stores.forEach(s => {
        if (s.id) {
          if (s.color) colorMap[s.id] = s.color;
          if (s.text_color) textColorMap[s.id] = s.text_color;
          if (s.category) catMap[s.id] = s.category;
          placeMap[s.id] = { x: s.logo_x, y: s.logo_y, scale: s.logo_scale };
        }
      });

      const grouped = {};
      benefits.forEach(b => {
        if (!b.storeId) return;
        if (!grouped[b.storeId]) {
          grouped[b.storeId] = {
            storeId:         b.storeId,
            storeName:       b.storeName || 'Tienda',
            storeLogo:       b.storeLogo || null,
            baseColor:       colorMap[b.storeId] || PALETTE[Object.keys(grouped).length % PALETTE.length],
            textColor:       textColorMap[b.storeId] || null,
            category:        catMap[b.storeId] || '',
            logoX:           placeMap[b.storeId]?.x,
            logoY:           placeMap[b.storeId]?.y,
            logoScale:       placeMap[b.storeId]?.scale,
            benefits:        [],
            featuredBenefit: null,
          };
        }
        grouped[b.storeId].benefits.push(b);
        if (!grouped[b.storeId].featuredBenefit || b.isNew) {
          grouped[b.storeId].featuredBenefit = b;
        }
      });

      const cards = Object.values(grouped);

      /*
        Una tarjeta por BENEFICIO, no una por tienda (Iván, 30-ago-2026: «que
        salgan todos los beneficios en la landing, que no se filtren»).

        Antes esto agrupaba por tienda y solo pintaba el `featuredBenefit`: una
        tienda con cinco beneficios enseñaba uno y los otros cuatro quedaban
        guardados en `grouped[].benefits` sin llegar nunca a la pantalla. De
        paso, es lo que hace visible el orden que el admin ya podía arrastrar en
        «Beneficios» — antes lo decidía el agrupado, no él.

        El orden lo fija el backend (`display_order`), así que aquí no se toca.
        Los beneficios sin tienda también entran: «todos» es todos.
      */
      const colorPorTienda = {};
      cards.forEach(c => { colorPorTienda[c.storeId] = c.baseColor; });

      setBenefitCards(benefits.map((b, i) => ({
        id:             b.id ?? `b-${i}`,
        storeName:      b.storeName || 'Tienda',
        headlineNumber: b.headlineNumber,
        headlineThin:   b.headlineThin,
        headlineStrong: b.headlineStrong,
        scope:          b.scope,
        allyLogo:       b.allyLogo || b.storeLogo || null,
        colorStart:     b.colorStart,
        colorEnd:       b.colorEnd,
        gradientAngle:  b.gradientAngle,
        storeColor:     colorPorTienda[b.storeId] || PALETTE[i % PALETTE.length],
      })));

      setStoreCards(cards);
      setBrandNames(cards.map(c => c.storeName));
      setCarouselReady(true);
    }).catch(() => setCarouselReady(true));
  }, []);

  /* Scroll tracking — activate after hero (~60 vh) */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Brand cycling in header */
  useEffect(() => {
    if (!scrolled || brandNames.length < 2) return;
    const id = setInterval(() => setBrandIdx(i => (i + 1) % brandNames.length), 2000);
    return () => clearInterval(id);
  }, [scrolled, brandNames.length]);

  /*
    El botón de la barra fija.

    Apuntaba a `#mp-pricing`, el ancla de la sección de precio — que se retiró.
    Un ancla que ya no existe no da error: `getElementById` devuelve null y el
    clic **no hace nada**, que es justo el defecto que más se nota al probar y
    el más difícil de reportar («le di y no pasó nada»).

    Por eso, si el ancla no está en la página, esto no se queda callado: lleva
    a los beneficios, que es a donde llevan todos los demás CTA del landing.
  */
  const handleNavCTA = () => {
    const route = config.navCTA?.route || '/beneficios';

    if (route.startsWith('#')) {
      const destino = document.getElementById(route.slice(1));
      if (destino) {
        destino.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      navigate('/beneficios');
      return;
    }

    navigate(route);
  };

  /* Render each section by type */
  const renderSection = (section) => {
    const { type, data, id } = section;

    switch (type) {
      case 'hero':
        return <HeroSection key={id} data={data} navigate={navigate} />;

      case 'discount_carousel':
        return carouselReady && benefitCards.length > 0
          ? (
            <InfiniteCarousel
              key={id}
              items={benefitCards}
              label={data?.label || 'Lo que vondi te da'}
              mostrarContador={false}
              renderItem={c => <DiscountCard card={c} />}
            />
          )
          : !carouselReady
            ? (
              <section key={id} style={{ paddingLeft: PEEK, paddingBottom: 24, display: 'flex', gap: GAP }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ flexShrink: 0, width: CARD_W, height: 340, borderRadius: 20, background: 'rgba(0,0,0,0.06)' }} />
                ))}
              </section>
            )
          : null;

      /* 'promo_carousel' (Destacados) se retiró: llevaba apagado en producción
         y su tarjeta era una cuarta variante del mismo molde. Si una config
         vieja todavía trae la sección, aquí no renderiza nada. */

      case 'marquee': {
        const configLogos  = (data?.logos ?? []).filter(l => l.active !== false);
        // Si no hay marquesina configurada en el admin, usamos los LOGOS reales de las
        // tiendas (imágenes), no solo el nombre en texto.
        const storeLogos = storeCards
          .filter(c => c.storeLogo)
          .map((c, i) => ({ id: `st-${i}`, name: c.storeName, imageUrl: c.storeLogo }));
        const fallbackLogos = storeLogos.length > 0
          ? storeLogos
          : brandNames.map((name, i) => ({ id: `fb-${i}`, name, imageUrl: '' }));
        const items = configLogos.length > 0 ? configLogos : fallbackLogos;
        return <BrandScroller key={id} logos={items} />;
      }

      case 'pay_less_intro':
        return <PayLessIntroSection key={id} data={data} navigate={navigate} />;

      case 'how_it_works':
        return <HowItWorksSection key={id} data={data} />;

      case 'savings_calculator':
        return <SavingsCalculatorSection key={id} data={data} />;

      case 'cta_ready':
        return <CTAReadySection key={id} data={data} navigate={navigate} />;

      /*
        `pricing` se retiró (Iván, 13-ago-2026): «quitar la última sección del
        landing donde ahora mismo sale lo de $277» y «que termine en acceder».
        Son la misma instrucción: el bloque de precio iba justo después del CTA
        de «Acceder», así que quitándolo la página termina en el botón.

        El `case` se queda, sin pintar nada: la configuración guardada todavía
        puede traer la sección, y sin esta rama caería en el `default` — que
        también devuelve null, pero sin decir por qué.
      */
      case 'pricing':
        return null;

      case 'info_block':
        return <InfoBlockSection key={id} data={data} />;

      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: VD.blanco, color: VD.tinta, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{CSS}</style>

      {/* ── Nav ── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        padding: '8px 18px',
        paddingTop: 'max(8px, env(safe-area-inset-top, 8px))',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? 'rgba(255,255,255,0.94)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? `1px solid ${VD.linea}` : 'none',
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}>

        {/*
          Izquierda: el wordmark arriba de todo, y al hacer scroll las marcas
          que van rotando.

          Antes aquí había un ícono de persona y **el logotipo no aparecía en
          ninguna parte del landing**: la página de entrada de la marca no
          enseñaba la marca. El acceso a la cuenta no se pierde — sigue en el
          hero («¿Ya eres Miembro? Acceder»), en el menú y en el ícono de la
          derecha mientras no se ha hecho scroll.
        */}
        {scrolled && brandNames.length > 0 ? (
          <span
            key={brandIdx}
            className="font-display"
            style={{
              fontWeight: 600, fontSize: 15, letterSpacing: '-0.02em',
              color: VD.tinta, whiteSpace: 'nowrap',
              animation: 'mp-brand-in 0.35s ease forwards',
            }}
          >
            {brandNames[brandIdx % brandNames.length]}
          </span>
        ) : (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            title="vondi"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <VondiLogo variant="gradiente" size={17} />
          </button>
        )}

        {/* Right: CTA (al hacer scroll) + hamburguesa (abre menú de secciones) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {scrolled ? (
            <button onClick={handleNavCTA} style={{
              height: 38, borderRadius: RADIO.pill, padding: '0 18px',
              background: config.navCTA?.color || GRAD.vivo,
              color: VD.blanco,
              fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.01em',
              border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              boxShadow: SOMBRA.sm,
            }}>
              {config.navCTA?.text || '¡Paga menos!'}
            </button>
          ) : (
            <button onClick={() => navigate('/login')} title="Iniciar sesión" aria-label="Iniciar sesión" style={{
              width: 42, height: 42, borderRadius: '50%',
              background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)',
              border: `1px solid ${VD.linea}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: SOMBRA.sm, cursor: 'pointer', flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={VD.tinta} strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
            </button>
          )}
          {/*
            La hamburguesa con atajos a las secciones se retiró (Iván,
            13-ago-2026). En una página que se recorre de un scroll, un índice
            duplicaba la navegación y competía con el único botón que importa:
            el de acceder.
          */}
        </div>
      </div>

      {/* ── Sections ── */}
      {sections.map(s => {
        const el = renderSection(s);
        if (!el) return null;
        // La marquesina nunca debe quedar tapada por los íconos fijos del header
        const extraTop = s.type === 'marquee' ? 64 : 0;
        return (
          <div key={s.id} id={`mp-section-${s.type}`} style={{ scrollMarginTop: 72, paddingTop: extraTop }}>
            {el}
          </div>
        );
      })}

      {/*
        Pie legal.

        Vive aquí porque el aviso de privacidad **solo era alcanzable desde el
        menú** que se acaba de retirar. Dejarlo sin salida no era opción: es un
        documento que la ley obliga a tener a la vista.
      */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, padding: '28px 24px 36px' }}>
        <button onClick={() => navigate('/terms')} style={{
          background: 'none', border: 'none', fontSize: 12.5, color: VD.pizarra,
          cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
        }}>
          Términos y Condiciones
        </button>
        <button onClick={() => navigate('/privacy')} style={{
          background: 'none', border: 'none', fontSize: 12.5, color: VD.pizarra,
          cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3,
        }}>
          Aviso de Privacidad
        </button>
      </div>
    </div>
  );
};

export default LandingPage;
